import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, MapPin, Star } from "lucide-react";

// Custom X (formerly Twitter) icon
function XIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Custom TikTok icon
function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.12Z" />
    </svg>
  );
}

// Google G logo
function GoogleGIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

const SOCIAL_LINKS = [
  { icon: Facebook, href: "https://www.facebook.com/dplususa/", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/dplususa/", label: "Instagram" },
  { icon: TikTokIcon, href: "https://www.tiktok.com/@dplususa", label: "TikTok" },
  { icon: Youtube, href: "https://www.youtube.com/channel/UClu4ioGfdy4h2ViDhJxsGOQ/videos", label: "YouTube" },
  { icon: XIcon, href: "https://x.com/dplus_usa", label: "X (Twitter)" },
];

const LOCATIONS = [
  {
    name: "Broward Store",
    line1: "3175 W Broward Blvd",
    line2: "Fort Lauderdale, FL 33312",
    mapsHref: "https://www.google.com/maps/search/?api=1&query=3175+W+Broward+Blvd+Fort+Lauderdale+FL+33312",
  },
  {
    name: "Swap Shop Store",
    line1: "3291 W Sunrise Blvd",
    line2: "Fort Lauderdale, FL 33311",
    mapsHref: "https://www.google.com/maps/search/?api=1&query=3291+W+Sunrise+Blvd+Fort+Lauderdale+FL+33311",
  },
];

const IMPORTANT_LINKS = [
  { label: "Blog", href: "/blog", external: false },
  { label: "About Let’s Print", href: "/about", external: false },
  { label: "Monthly Promos", href: "/promo", external: false },
  { label: "Join Our Community", href: "https://nas.com/letsgobeyond", external: true },
  { label: "Contact & Support", href: "/help", external: false },
  { label: "Create a ticket", href: "#", external: false },
  { label: "Send Your File", href: "/send-your-file", external: false },
];

const REVIEWS_URL = "https://share.google/opCxxsZ3LhI4wDkzN";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-white border-t border-neutral-200 mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand + description */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-5" aria-label="Let’s Print home">
              <img
                src="https://customer-assets.emergentagent.com/job_print-migration-1/artifacts/cxdbl1o1_LP%20FINAL%20LOGO%20WEBSITE.png"
                alt="Let’s Print"
                className="h-12 w-auto object-contain"
                draggable="false"
              />
            </Link>
            <p
              data-testid="footer-description"
              className="text-sm text-neutral-600 max-w-sm leading-relaxed mb-6"
            >
              Let’s Print is A High Tech, Full Service Print Shop, Website &amp; App
              Development &amp; Digital Marketing company in South Florida.
            </p>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="w-9 h-9 border border-neutral-300 text-neutral-700 hover:border-[#2C66E3] hover:text-[#2C66E3] rounded-full flex items-center justify-center transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Visit Us — Locations */}
          <div className="lg:col-span-3">
            <h4 className="font-bold text-[#1a1a1a] mb-5 text-xs uppercase tracking-[0.14em]">
              Visit Us
            </h4>
            <ul className="space-y-5 text-sm">
              {LOCATIONS.map((loc) => (
                <li key={loc.name}>
                  <a
                    href={loc.mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-location-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex gap-2.5 hover:text-[#2C66E3] transition-colors"
                  >
                    <MapPin className="w-4 h-4 text-[#2C66E3] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-[#1a1a1a] group-hover:text-[#2C66E3] transition-colors">
                        {loc.name}
                      </p>
                      <p className="text-neutral-600 leading-snug">{loc.line1}</p>
                      <p className="text-neutral-600 leading-snug">{loc.line2}</p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Important Links */}
          <div className="lg:col-span-2">
            <h4 className="font-bold text-[#1a1a1a] mb-5 text-xs uppercase tracking-[0.14em]">
              Important Links
            </h4>
            <ul className="space-y-2.5 text-sm">
              {IMPORTANT_LINKS.map((l) => (
                <li key={l.label}>
                  {l.external ? (
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-neutral-600 hover:text-[#2C66E3] transition-colors"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      to={l.href}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-neutral-600 hover:text-[#2C66E3] transition-colors"
                    >
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Google Reviews block */}
          <div className="lg:col-span-3">
            <h4 className="font-bold text-[#1a1a1a] mb-5 text-xs uppercase tracking-[0.14em]">
              Trusted by Locals
            </h4>
            <a
              href={REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google-reviews-card"
              className="block group rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#2C66E3] hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <GoogleGIcon className="w-5 h-5" />
                <span className="text-[11px] font-bold text-neutral-600 uppercase tracking-[0.14em]">
                  Google Reviews
                </span>
              </div>

              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-3xl font-black text-[#1a1a1a] leading-none">4.9</span>
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                  ))}
                </div>
              </div>

              <p className="text-sm text-neutral-600 mb-3">
                Based on <span className="font-bold text-[#1a1a1a]">494 reviews</span>
              </p>

              <p className="text-xs font-bold text-[#1a1a1a] leading-snug">
                The Most Preferred Print Shop in Broward County
              </p>

              <p className="mt-3 text-xs font-semibold text-[#2C66E3] group-hover:underline">
                Read reviews →
              </p>
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-neutral-200 mt-12 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-neutral-500">
          <p>
            © {new Date().getFullYear()} Let’s Print Inc. All rights
            reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className="hover:text-[#1a1a1a] transition-colors"
            >
              Privacy Policy
            </Link>
            <a
              href="https://square.link/u/xytFUguv"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-pay-now-link"
              className="hover:text-[#1a1a1a] transition-colors"
            >
              Pay Now
            </a>
            <Link
              to="/terms-of-service"
              data-testid="footer-terms-link"
              className="hover:text-[#1a1a1a] transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/privacy-policy"
              data-testid="footer-cookies-link"
              className="hover:text-[#1a1a1a] transition-colors"
            >
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
